import { VehicleFilter } from '../util/oilHelper';
import { Vehicle, Vehicle2 } from './Vehicle';

export enum ServiceType {
  primary = 'PRIMARY',
  addOn = 'ADD_ON',
  oilChange = 'OIL_CHANGE',
  subscription = 'SUBSCRIPTION',
}

type service = {
  id: number
  serviceType: {
    name: string
  }
  invoiceDescription: string
  nameKey?: string
}

export type LineItem = {
  id: number
  price: number
  tax: number
  quantity: number
  name: string
  description: string
  service: service
  serviceId?: number
  discountedPrice?: number
}

export type DiscountCode = {
  issuer: string
  discountType: string
  discountAmount: number
}

export type WorkOrderDiscountCode = {
  amountApplied: number
  discountCodeId: string
  discountCode?: DiscountCode
}

export type ServiceItem = {
  id: number
  price: number
  tax: number
  quantity: number
  name: string
  nameKey?: string
  description: string
  service?: service
  discountedPrice?: number
  serviceId?: number
}

export type InventoryPartItem = {
  id: number
  quantity: number
  partName: string
  partType: string
}

export type Notes = {
  notes: string
}

export type PoNumber = {
  poNumber: string
}

export enum WorkOrderStatus {
  // A fleet forecast the scheduler parked beyond its horizon sits PENDING.
  pending = 'PENDING',
  assigned = 'ASSIGNED',
  confirmed = 'CONFIRMED',
  complete = 'COMPLETE',
}

export enum SubscriptionStatus {
  active = 'ACTIVE',
  pending = 'PENDING',
  cancelled = 'CANCELLED',
}

export interface VehicleSubscription {
  id: number
  subscriptionTypeId: number
  status: SubscriptionStatus
}

export interface WorkOrder {
  id: number
  scheduledAt: Date
  completedAt?: Date
  isInspected?: boolean
  isConfirmed?: boolean
  poNumber?: string
  user: {
    id: number
    email: string
    name: string
    discountAmount: string
    discountType: string
  }
  address: {
    lat: number;
    lng: number;
    city: string
    province: string
    provinceCode: string
    street: string
    countryCode: string
    postalCode: string
  }
  contact: {
    email: string;
    name: string;
    phoneNumber: string;
  }
  franchise: {
    id: number;
    name: string;
    managerEmail: string;
    phoneNumber: string;
  }
  userVehicle: {
    id: number
    vin: string
    name: string
    subscriptions?: [VehicleSubscription]
    smartcarVehicleId?: string
    tireSizeOverride?: { id: number, size: string } | null
  }
  lineItems: [LineItem]
  vehicle: Vehicle2
  notes: string
  status: WorkOrderStatus
  unavailabilityReason: string
  cancelReason: string
  proofOfCancelLocation: string
  invoice: {
    status: string
    billedAmount: number
    id: number
    invoiceNumber: string
  }
  van: {
    id: number
    name: string
    userVans: Array<{
      user: {
        name?: string;
        email: string;
      };
    }>;
  }
  workOrderDiscountCode: WorkOrderDiscountCode,
  userCreditTransactions: UserCreditTransaction[],
  // In-house (fleet self-logged) service: FV-#### and who did it.
  selfLogged?: boolean | null;
  fleetVisitNumber?: number | null;
  selfLoggedByName?: string | null;
}

export interface Inspection {
  id: number | null;
  tireConditionFrontLeft: number | null;
  tireConditionFrontRight: number | null;
  tireConditionRearLeft: number | null;
  tireConditionRearRight: number | null;
  brakeConditionFrontLeft: number | null;
  brakeConditionFrontRight: number | null;
  brakeConditionRearLeft: number | null;
  brakeConditionRearRight: number | null;
  windshieldsCondition: number | null;
  notes: string | null;
  tireIdNumberFrontLeft: number | null;
  tireIdNumberFrontRight: number | null;
  tireIdNumberRearLeft: number | null;
  tireIdNumberRearRight: number | null;
  checkedFrontLeft: boolean | null;
  checkedFrontRight: boolean | null;
  checkedRearLeft: boolean | null;
  checkedRearRight: boolean | null;
  windshieldFluid: boolean | null;
  brakeFluid: boolean | null;
  leakInspection: boolean | null;
  tireSize: string | null;
  engineHours: number | null;
  tireBrandId: number | null;
  tireType: string | null;
  isDually?: boolean | null;
  tireConditionRearLeftInner?: number | null;
  tireConditionRearRightInner?: number | null;
  tireIdNumberRearLeftInner?: number | null;
  tireIdNumberRearRightInner?: number | null;
  // Brand per wheel position (names); blank = same as tireBrandId's brand.
  tireBrandFrontLeft?: string | null;
  tireBrandFrontRight?: string | null;
  tireBrandRearLeft?: string | null;
  tireBrandRearRight?: string | null;
  tireBrandRearLeftInner?: string | null;
  tireBrandRearRightInner?: string | null;
  inspectionType: string;
  // JSON: outer/middle/inner tread, pads, full TINs, photos
  // (util/inspectionMeasurements.ts).
  measurementsJson?: string | null;
  awsLink?: AwsLink,
}

export interface AwsLink {
  id: number | null;
  vinLink: string | null;
  mileageLink: string | null;
  oilCapLink: string | null;
  dipstickLocation: string | null;
  vehicleLocationFrontLeft: string | null;
  vehicleLocationFrontRight: string | null;
  vehicleLocationRearLeft: string | null;
  vehicleLocationRearRight: string | null;
  tinLocationFrontLeft: string | null;
  tinLocationFrontRight: string | null;
  tinLocationRearLeft: string | null;
  tinLocationRearRight: string | null;
  oilFilterLocation: string | null;
  oilLifeLocation: string | null;
  oilLifeResetLocation: string | null;
  windshieldLocation?: string | null;
}

export interface WorkOrderDetail {
  id: number
  scheduledAt: Date
  completedAt?: Date
  isInspected?: boolean
  isConfirmed?: boolean
  poNumber?: string
  accessOutcome?: string
  accessOutcomeContact?: string
  user: {
    id: number
    email: string
    name: string
    discountAmount: string
    discountType: string
  }
  address: {
    lat: number;
    lng: number;
    city: string
    province: string
    provinceCode: string
    street: string
    countryCode: string
    postalCode: string
  }
  contact: {
    email: string;
    name: string;
    phoneNumber: string;
  }
  franchise: {
    id: number;
    name: string;
    managerEmail: string;
    phoneNumber: string;
  }
  userVehicle: {
    id: number
    vin: string
    name: string
    subscriptions?: [VehicleSubscription]
    smartcarVehicleId?: string
    tireSizeOverride?: { id: number, size: string } | null
  }
  lineItems: [LineItem]
  vehicle: Vehicle2
  notes: string
  status: WorkOrderStatus
  unavailabilityReason: string
  cancelReason: string
  proofOfCancelLocation: string
  invoice: {
    status: string
    billedAmount: number
    id: number
    invoiceNumber: string
  }
  van: {
    id: number
    name: string
    userVans: Array<{
      user: {
        name?: string;
        email: string;
      };
    }>;
  }
  workOrderDiscountCode: WorkOrderDiscountCode,
  userCreditTransactions: UserCreditTransaction[],
  inspections?: [Inspection],
}

export interface GroupedWorkOrders {
  [key: string]: WorkOrder[]
}

export interface Appointment {
  id: number
  vehicle: Vehicle
  lineItems: [LineItem]
  scheduledAt: Date
}

export interface UserCreditTransaction {
  type: 'DEBIT' | 'CREDIT';
  amount: number;
  id: number;
}

export interface WorkOrderProps {
  id: number
  user: string
  userId: number,
  accessOutcome?: string,
  accessOutcomeContact?: string,
  userDiscountType: string,
  userDiscountAmount: string,
  address: string
  addressLat: number
  addressLng: number
  contactName: string
  contactEmail: string
  contactPhone: string
  vehicle: string
  workOrderDiscountCode: WorkOrderDiscountCode
  userCreditTransactions: UserCreditTransaction[]
  oilWeight: string
  oilVolume?: number
  batteryGroupSize?: string | null
  tireSize?: string | null
  lugTorqueFtLb?: string | null
  drainPlugHex?: string | null
  lugNutHex?: string | null
  tirePressurePsi?: string | null
  recommendedOilId?: number
  recommendedOilFilter?: string
  filters?: VehicleFilter[]
  vehicleId: number
  userVehicleId: number
  vin?: string
  name?:string
  franchise: string
  franchiseId: number
  vanId?: number
  vanName?: string
  technicianName?: string
  technicianEmail?: string
  scheduledAt: string
  notes: string
  cancelReason: string
  proofOfCancelLocation: string
  status: WorkOrderStatus
  invoiceStatus: string
  billedAmount: string
  invoiceId: number
  invoiceNumber: string
  services: Array<string>
  lineitems: Array<LineItem>
  unavailabilityReason?: string
  vehicleSubscription?: VehicleSubscription
  poNumber?: string
  engine: string
  year: string
  model: string
  make: string
  smartcarVehicleId?: string
  inspections?: Array<Inspection>
}

export interface WorkOrderReason {
  workOrder: WorkOrder,
  reason: string,
}

export interface Franchise {
  id: number;
  name: string;
}

export interface Vans {
  id: number;
  name: string;
}

export interface FranchiseVans {
  id: number;
  name: string;
  vans: Vans[];
}

export type MileageEventType = 'OIL_CHANGE' | 'USER' | 'SERVICE'
